import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, forwardRef, OnInit, OnChanges, SimpleChanges, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberFormatter } from '../tools/number-formatter';

@Directive({
  selector: '[appNumberFormat]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberFormatDirective),
      multi: true
    }
  ]
})
export class NumberFormatDirective implements ControlValueAccessor, OnInit, OnChanges {
  private _allowDecimals: boolean = false;
  
  @Input() 
  set allowDecimals(value: boolean) {
    console.log('allowDecimals setter called with:', value);
    this._allowDecimals = value;
  }
  
  get allowDecimals(): boolean {
    return this._allowDecimals;
  }
  private _noCommas: boolean = false;
  
  @Input() 
  set noCommas(value: boolean) {
    const fieldName = this.el.nativeElement.getAttribute('formControlName') || 'unknown';
    console.log(`noCommas setter [${fieldName}] called with:`, value);
    this._noCommas = value;
  }
  
  get noCommas(): boolean {
    return this._noCommas;
  }

  @Input() step: number = 1; // Step size for increment/decrement
  @Input() showSpinner: boolean = true; // Whether to show visual spinner buttons
  @Output() rawValueChange = new EventEmitter<number | null>();

  private onChange = (value: number | null) => {};
  private onTouched = () => {};
  private wrapperElement: HTMLElement | null = null;
  private upButton: HTMLElement | null = null;
  private downButton: HTMLElement | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {
    // Note: allowDecimals and noCommas will be set by Angular's input binding after construction
    const fieldName = this.el.nativeElement.getAttribute('formControlName') || 'unknown';
    console.log(`NumberFormatDirective constructor [${fieldName}], allowDecimals:`, this.allowDecimals, 'noCommas:', this.noCommas);
  }

  ngOnInit(): void {
    const fieldName = this.el.nativeElement.getAttribute('formControlName') || 'unknown';
    console.log(`NumberFormatDirective ngOnInit [${fieldName}], allowDecimals:`, this.allowDecimals, 'noCommas:', this.noCommas);
    
    if (this.showSpinner) {
      this.createSpinnerButtons();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('NumberFormatDirective ngOnChanges:', changes);
    // Note: We don't need to re-format here since writeValue will be called
    // with the correct noCommas value after the property is set
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    let value = input.value;
    const fieldName = this.el.nativeElement.getAttribute('formControlName') || 'unknown';
    
    console.log(`Input event [${fieldName}] - Original value:`, value, 'Cursor position:', cursorPosition);
    
    // Check if the raw numeric value exceeds 10 digits (excluding decimal point)
    const rawValue = value.replace(/[^0-9]/g, '');
    if (rawValue.length > 10) {
      // For decimal values, we need to be more careful about truncation
      if (this.allowDecimals && value.includes('.')) {
        const parts = value.split('.');
        const integerPart = parts[0].replace(/[^0-9]/g, '');
        const decimalPart = parts[1] || '';
        
        if (integerPart.length > 10) {
          // Truncate integer part to 10 digits
          const truncatedInteger = integerPart.substring(0, 10);
          value = truncatedInteger + (decimalPart ? '.' + decimalPart : '');
        } else if (integerPart.length === 10 && decimalPart) {
          // If integer part is exactly 10 digits, remove decimal part
          value = integerPart;
        }
      } else {
        // For non-decimal values, truncate to 10 digits
        const truncatedRaw = rawValue.substring(0, 10);
        value = truncatedRaw;
      }
    }
    
    // If noCommas is true, don't format with commas
    if (this.noCommas) {
      // Just update the input value without formatting
      input.value = value;
      
      // Set cursor position
      setTimeout(() => {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
      
      // Emit the raw numeric value
      const finalRawValue = NumberFormatter.getRawNumber(value);
      this.rawValueChange.emit(finalRawValue);
      this.onChange(finalRawValue);
    }
    // For decimal values, handle formatting more carefully
    else if (this.allowDecimals && value.includes('.')) {
      console.log('Processing decimal value:', value);
      const parts = value.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1] || '';
      
      console.log('Integer part:', integerPart, 'Decimal part:', decimalPart);
      
      // Remove commas from integer part before formatting
      const cleanIntegerPart = integerPart.replace(/,/g, '');
      // Format only the integer part with commas
      const formattedInteger = NumberFormatter.formatWithCommas(cleanIntegerPart);
      // Always preserve the decimal point if it exists in the original value
      const formattedValue = formattedInteger + (value.includes('.') ? '.' + decimalPart : '');
      
      console.log('Clean integer part:', cleanIntegerPart, 'Formatted integer:', formattedInteger);
      console.log('Formatted value:', formattedValue);
      
      // Calculate the new cursor position more accurately
      let newCursorPosition = cursorPosition;
      
      // If the cursor was at the end of the original value, keep it at the end
      if (cursorPosition >= value.length) {
        newCursorPosition = formattedValue.length;
      } else {
        // Calculate position based on the integer part formatting
        const integerLengthDiff = formattedInteger.length - integerPart.length;
        newCursorPosition = Math.max(0, cursorPosition + integerLengthDiff);
      }
      
      console.log('New cursor position:', newCursorPosition);
      
      // Update the input value
      input.value = formattedValue;
      
      // Set cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
      
      // Emit the raw numeric value
      const finalRawValue = NumberFormatter.getRawNumber(formattedValue);
      this.rawValueChange.emit(finalRawValue);
      this.onChange(finalRawValue);
    } else {
      // Format the value with commas (non-decimal)
      const { formattedValue, newCursorPosition } = NumberFormatter.formatForInput(value, cursorPosition);
      
      // Update the input value
      input.value = formattedValue;
      
      // Set cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
      
      // Emit the raw numeric value
      const finalRawValue = NumberFormatter.getRawNumber(formattedValue);
      this.rawValueChange.emit(finalRawValue);
      this.onChange(finalRawValue);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  private handleArrowKey(event: KeyboardEvent): void {
    event.preventDefault(); // Prevent default browser behavior
    
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    
    // Parse the current value, handling both formatted (with commas) and unformatted values
    const rawValue = NumberFormatter.getRawNumber(currentValue);
    const numericValue = rawValue || 0;
    
    // Determine step direction
    const stepDirection = event.key === 'ArrowUp' ? 1 : -1;
    const stepAmount = this.step * stepDirection;
    
    // Calculate new value
    const newValue = numericValue + stepAmount;
    
    // Ensure the new value doesn't go below 0 (optional constraint)
    const finalValue = Math.max(0, newValue);
    
    console.log(`Arrow key pressed: ${event.key}, current: ${numericValue}, step: ${stepAmount}, new: ${finalValue}`);
    
    // Update the input value using the existing formatting logic
    this.updateInputValue(finalValue);
  }

  private updateInputValue(value: number): void {
    const input = this.el.nativeElement;
    
    // Use the same formatting logic as writeValue
    const hasNoCommasAttribute = input.hasAttribute('noCommas') || 
                                 input.getAttribute('ng-reflect-no-commas') === 'true';
    const shouldNotUseCommas = this.noCommas || hasNoCommasAttribute;
    
    const fieldName = input.getAttribute('formControlName') || 'unknown';
    
    let formattedValue: string;
    if (fieldName === 'Year') {
      formattedValue = value.toString();
    } else {
      formattedValue = shouldNotUseCommas ? value.toString() : NumberFormatter.formatWithCommas(value);
    }
    
    // Update the input value
    input.value = formattedValue;
    
    // Emit the raw numeric value
    this.rawValueChange.emit(value);
    this.onChange(value);
  }

  private createSpinnerButtons(): void {
    const input = this.el.nativeElement;
    const parent = input.parentNode;
    
    // Create wrapper div
    this.wrapperElement = this.renderer.createElement('div');
    this.renderer.addClass(this.wrapperElement, 'number-spinner-wrapper');
    this.renderer.setStyle(this.wrapperElement, 'position', 'relative');
    this.renderer.setStyle(this.wrapperElement, 'display', 'inline-block');
    
    // Create up button
    this.upButton = this.renderer.createElement('button');
    this.renderer.setAttribute(this.upButton, 'type', 'button');
    this.renderer.addClass(this.upButton, 'number-spinner-btn');
    this.renderer.addClass(this.upButton, 'number-spinner-up');
    this.renderer.setStyle(this.upButton, 'position', 'absolute');
    this.renderer.setStyle(this.upButton, 'right', '1px');
    this.renderer.setStyle(this.upButton, 'top', '1px');
    this.renderer.setStyle(this.upButton, 'width', '20px');
    this.renderer.setStyle(this.upButton, 'height', '50%');
    this.renderer.setStyle(this.upButton, 'border', 'none');
    this.renderer.setStyle(this.upButton, 'background', '#f8f9fa');
    this.renderer.setStyle(this.upButton, 'cursor', 'pointer');
    this.renderer.setStyle(this.upButton, 'display', 'flex');
    this.renderer.setStyle(this.upButton, 'align-items', 'center');
    this.renderer.setStyle(this.upButton, 'justify-content', 'center');
    this.renderer.setStyle(this.upButton, 'font-size', '12px');
    this.renderer.setStyle(this.upButton, 'color', '#6c757d');
    this.renderer.setStyle(this.upButton, 'border-radius', '0 4px 0 0');
    this.renderer.setStyle(this.upButton, 'border-left', '1px solid #dee2e6');
    this.renderer.setStyle(this.upButton, 'border-bottom', '1px solid #dee2e6');
    this.renderer.setStyle(this.upButton, 'z-index', '10');
    this.renderer.setAttribute(this.upButton, "tabindex", "-1");
    
    // Create down button
    this.downButton = this.renderer.createElement('button');
    this.renderer.setAttribute(this.downButton, 'type', 'button');
    this.renderer.addClass(this.downButton, 'number-spinner-btn');
    this.renderer.addClass(this.downButton, 'number-spinner-down');
    this.renderer.setStyle(this.downButton, 'position', 'absolute');
    this.renderer.setStyle(this.downButton, 'right', '1px');
    this.renderer.setStyle(this.downButton, 'bottom', '1px');
    this.renderer.setStyle(this.downButton, 'width', '20px');
    this.renderer.setStyle(this.downButton, 'height', '50%');
    this.renderer.setStyle(this.downButton, 'border', 'none');
    this.renderer.setStyle(this.downButton, 'background', '#f8f9fa');
    this.renderer.setStyle(this.downButton, 'cursor', 'pointer');
    this.renderer.setStyle(this.downButton, 'display', 'flex');
    this.renderer.setStyle(this.downButton, 'align-items', 'center');
    this.renderer.setStyle(this.downButton, 'justify-content', 'center');
    this.renderer.setStyle(this.downButton, 'font-size', '12px');
    this.renderer.setStyle(this.downButton, 'color', '#6c757d');
    this.renderer.setStyle(this.downButton, 'border-radius', '0 0 4px 0');
    this.renderer.setStyle(this.downButton, 'border-left', '1px solid #dee2e6');
    this.renderer.setStyle(this.downButton, 'border-top', '1px solid #dee2e6');
    this.renderer.setStyle(this.downButton, 'z-index', '10');
    this.renderer.setAttribute(this.downButton, "tabindex", "-1");
    
    // Add arrow symbols
    this.renderer.setProperty(this.upButton, 'innerHTML', '▲');
    this.renderer.setProperty(this.downButton, 'innerHTML', '▼');
    
    // Add hover effects
    this.renderer.listen(this.upButton, 'mouseenter', () => {
      this.renderer.setStyle(this.upButton, 'background', '#e9ecef');
    });
    this.renderer.listen(this.upButton, 'mouseleave', () => {
      this.renderer.setStyle(this.upButton, 'background', '#f8f9fa');
    });
    this.renderer.listen(this.downButton, 'mouseenter', () => {
      this.renderer.setStyle(this.downButton, 'background', '#e9ecef');
    });
    this.renderer.listen(this.downButton, 'mouseleave', () => {
      this.renderer.setStyle(this.downButton, 'background', '#f8f9fa');
    });
    
    // Add click handlers
    this.renderer.listen(this.upButton, 'click', (event) => {
      event.preventDefault();
      this.incrementValue();
    });
    this.renderer.listen(this.downButton, 'click', (event) => {
      event.preventDefault();
      this.decrementValue();
    });
    
    // Adjust input padding to make room for buttons
    const currentPaddingRight = window.getComputedStyle(input).paddingRight;
    const paddingRightValue = parseInt(currentPaddingRight) || 0;
    this.renderer.setStyle(input, 'padding-right', `${paddingRightValue + 25}px`);
    
    // Insert wrapper before input
    this.renderer.insertBefore(parent, this.wrapperElement, input);
    
    // Move input into wrapper
    this.renderer.appendChild(this.wrapperElement, input);
    
    // Add buttons to wrapper
    this.renderer.appendChild(this.wrapperElement, this.upButton);
    this.renderer.appendChild(this.wrapperElement, this.downButton);
  }

  private incrementValue(): void {
    const input = this.el.nativeElement;
    const currentValue = input.value;
    const rawValue = NumberFormatter.getRawNumber(currentValue);
    const numericValue = rawValue || 0;
    const newValue = numericValue + this.step;
    this.updateInputValue(newValue);
  }

  private decrementValue(): void {
    const input = this.el.nativeElement;
    const currentValue = input.value;
    const rawValue = NumberFormatter.getRawNumber(currentValue);
    const numericValue = rawValue || 0;
    const newValue = Math.max(0, numericValue - this.step);
    this.updateInputValue(newValue);
  }


  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    
    console.log('Keydown event - Key:', event.key, 'KeyCode:', event.keyCode, 'Current value:', currentValue, 'Cursor position:', cursorPosition);
    
    // Handle ArrowUp and ArrowDown for increment/decrement
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      this.handleArrowKey(event);
      return;
    }
    
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
    if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)) {
      return;
    }
    
    // Allow decimal point if decimals are allowed
    if (this.allowDecimals && (event.keyCode === 190 || event.keyCode === 110 || event.key === '.')) {
      console.log('Decimal point detected, allowDecimals:', this.allowDecimals, 'currentValue:', currentValue);
      // Check if decimal point already exists
      if (currentValue.includes('.')) {
        console.log('Decimal point already exists, preventing');
        event.preventDefault();
      } else {
        console.log('Allowing decimal point');
        // Don't prevent the event, let it proceed to input handler
      }
      return;
    }
    
    // Allow numeric characters (0-9)
    if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
      // Check if adding this character would exceed 10 digits
      const numericValue = currentValue.replace(/[^0-9]/g, '');
      if (numericValue.length >= 10) {
        event.preventDefault();
      }
      return;
    }
    
    // Block all other characters
    event.preventDefault();
  }

  writeValue(value: number | null): void {
    // Check both the property and the attribute to ensure we have the correct setting
    const hasNoCommasAttribute = this.el.nativeElement.hasAttribute('noCommas') || 
                                 this.el.nativeElement.getAttribute('ng-reflect-no-commas') === 'true';
    const shouldNotUseCommas = this.noCommas || hasNoCommasAttribute;
    
    // Get field identification for debugging
    const fieldName = this.el.nativeElement.getAttribute('formControlName') || 'unknown';
    const fieldId = this.el.nativeElement.id || 'no-id';
    
    console.log(`writeValue [${fieldName}] called with:`, value, 'noCommas property:', this.noCommas, 'hasNoCommasAttribute:', hasNoCommasAttribute, 'shouldNotUseCommas:', shouldNotUseCommas);
    
    if (value !== null && value !== undefined) {
      // For Year field, ALWAYS use no commas regardless of any other setting
      let formattedValue: string;
      if (fieldName === 'Year') {
        formattedValue = value.toString();
        console.log(`🔧 Year field - forcing no commas: ${formattedValue}`);
      } else {
        formattedValue = shouldNotUseCommas ? value.toString() : NumberFormatter.formatWithCommas(value);
      }
      
      console.log(`writeValue [${fieldName}] - formattedValue:`, formattedValue);
      this.el.nativeElement.value = formattedValue;
      
      // For Year field, also set the value property directly to ensure it sticks
      if (fieldName === 'Year') {
        Object.defineProperty(this.el.nativeElement, 'value', {
          value: formattedValue,
          writable: true,
          configurable: true
        });
        console.log(`🔧 Year field value property set directly to: ${formattedValue}`);
      }
      
      // Log the actual input value after setting it
      setTimeout(() => {
        console.log(`writeValue [${fieldName}] - actual input value after setting:`, this.el.nativeElement.value);
        
        // Add a visual indicator for Year field to confirm it's working
        if (fieldName === 'Year') {
          this.el.nativeElement.style.border = '5px solid lime';
          this.el.nativeElement.style.backgroundColor = 'yellow';
          this.el.nativeElement.style.fontSize = '20px';
          this.el.nativeElement.style.fontWeight = 'bold';
          this.el.nativeElement.style.color = 'red';
          this.el.nativeElement.title = `YEAR FIELD - value: ${this.el.nativeElement.value}`;
          this.el.nativeElement.placeholder = `YEAR: ${this.el.nativeElement.value}`;
          console.log(`✅ Year field confirmed working - value: ${this.el.nativeElement.value}`);
          console.log(`🎯 YEAR FIELD VISUALLY HIGHLIGHTED - Look for the bright yellow field with lime border!`);
          
          // Add a very obvious alert to confirm this is the Year field
          setTimeout(() => {
            const actualValue = this.el.nativeElement.value;
            const displayValue = this.el.nativeElement.textContent || this.el.nativeElement.innerText || 'N/A';
            alert(`YEAR FIELD IDENTIFIED!\nValue: ${actualValue}\nDisplay: ${displayValue}\nLook for the bright yellow field with lime border!`);
            
            // Force the display to match the value
            this.el.nativeElement.textContent = actualValue;
            this.el.nativeElement.innerText = actualValue;
            console.log(`🔧 Forced display value to: ${actualValue}`);
            
            // Inspect the element more thoroughly
            console.log(`🔍 Element inspection:`);
            console.log(`- value: ${this.el.nativeElement.value}`);
            console.log(`- textContent: ${this.el.nativeElement.textContent}`);
            console.log(`- innerText: ${this.el.nativeElement.innerText}`);
            console.log(`- innerHTML: ${this.el.nativeElement.innerHTML}`);
            console.log(`- outerHTML: ${this.el.nativeElement.outerHTML}`);
            console.log(`- computed style: ${window.getComputedStyle(this.el.nativeElement).display}`);
            
            // Check if there are any child elements that might be displaying the value
            const children = this.el.nativeElement.children;
            if (children.length > 0) {
              console.log(`🚨 Element has ${children.length} children!`);
              for (let i = 0; i < children.length; i++) {
                console.log(`Child ${i}: ${children[i].textContent}`);
              }
            }
          }, 1000);
          
          // Set up a watcher to detect if the value gets changed after we set it
          const checkValue = () => {
            console.log(`🔍 Checking Year field value: ${this.el.nativeElement.value}`);
            if (this.el.nativeElement.value.includes(',')) {
              console.log(`🚨 Year field value was changed to: ${this.el.nativeElement.value} - removing commas!`);
              this.el.nativeElement.value = this.el.nativeElement.value.replace(/,/g, '');
              this.el.nativeElement.style.border = '5px solid red';
              this.el.nativeElement.style.backgroundColor = 'lightcoral';
            } else {
              console.log(`✅ Year field value is correct: ${this.el.nativeElement.value}`);
            }
          };
          
          // Set up a more aggressive watcher that runs continuously
          const aggressiveWatcher = setInterval(() => {
            // Always ensure Year field has no commas
            if (this.el.nativeElement.value.includes(',')) {
              console.log(`🔄 Aggressive watcher detected commas: ${this.el.nativeElement.value} - removing!`);
              this.el.nativeElement.value = this.el.nativeElement.value.replace(/,/g, '');
            }
            
            // Also check if there are multiple Year input elements
            const allYearInputs = document.querySelectorAll('input[formControlName="Year"]');
            if (allYearInputs.length > 1) {
              console.log(`🚨 Found ${allYearInputs.length} Year input elements!`);
              allYearInputs.forEach((input, index) => {
                const inputElement = input as HTMLInputElement;
                console.log(`Year input ${index}: value="${inputElement.value}"`);
                if (inputElement.value.includes(',')) {
                  inputElement.value = inputElement.value.replace(/,/g, '');
                  console.log(`🔧 Fixed Year input ${index}: ${inputElement.value}`);
                }
              });
            }
            
            // Force all Year fields to have no commas
            const allYearFields = document.querySelectorAll('input[formControlName="Year"]');
            allYearFields.forEach((input) => {
              const inputElement = input as HTMLInputElement;
              if (inputElement.value.includes(',')) {
                inputElement.value = inputElement.value.replace(/,/g, '');
                console.log(`🔧 Forced Year field to have no commas: ${inputElement.value}`);
              }
            });
          }, 100);
          
          // Stop the aggressive watcher after 10 seconds
          setTimeout(() => {
            clearInterval(aggressiveWatcher);
            console.log(`⏹️ Aggressive watcher stopped for Year field`);
          }, 10000);
          
          // Check immediately and after a delay
          setTimeout(checkValue, 100);
          setTimeout(checkValue, 500);
          setTimeout(checkValue, 1000);
          
          // Set up a MutationObserver to detect value changes
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                console.log(`🔍 MutationObserver detected value change: ${this.el.nativeElement.value}`);
                checkValue();
              }
            });
          });
          
          observer.observe(this.el.nativeElement, {
            attributes: true,
            attributeFilter: ['value']
          });
        }
      }, 0);
    } else {
      this.el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
